/**
 * The shape of one call to `authors-api`, and nothing else.
 *
 * WHY THIS FILE EXISTS AT ALL. The API does not exist yet — M0 ships no operation — so every
 * screen has to talk to something today that becomes the real thing tomorrow without being
 * rewritten. This module is the narrow waist that makes that a one-line change: everything above
 * it (`authors-api.ts`, the screens) is written against the GENERATED types and never touches
 * `fetch`; everything below it is one of two interchangeable implementations,
 * `./fetch-transport.ts` and `./mock-transport.ts`, chosen in exactly one place, `./transport.ts`.
 *
 * TWO RULES FROM THE CORPUS ARE BAKED INTO THE TYPES BELOW RATHER THAN LEFT TO A CONVENTION.
 *
 * 1. BEARER ONLY, NEVER A COOKIE. The cabinet is served from `authors.moonx.app` and calls
 *    `moonx.art/authors-api/*` cross-origin (adr/0002-edge-addressing.md). That surface is
 *    "bearer tokens only" and no cookie exists on this path, so `HttpRequest` carries a
 *    `bearerToken` field and has nowhere to put a credential of any other kind. The cookie session
 *    belongs to ADMIN, which shares an origin with its own API.
 *
 * 2. SOME OPERATIONS CARRY NO CREDENTIAL ON PURPOSE. `exchangeCabinetHandoffCode` declares
 *    `security: []` — "the code IS the credential, and a caller who had a Firebase token would not
 *    need the hand-off" (docs/design/07-api/authors-api.yaml, carried into the generated types
 *    as an operation with no security requirement). So `bearerToken` is optional here, and the
 *    typed operation layer decides which calls set it.
 *
 * THE RESPONSE IS DELIBERATELY NOT PARSED HERE. A transport returns status, headers and an
 * unparsed body; `./failure.ts` decides what a non-2xx MEANS and `./authors-api.ts` narrows a 2xx
 * to its generated type. Keeping those three apart is what lets the 401/503 rule
 * (adr/0010-firebase-integration.md) be one small pure function with a test.
 */

/** The verbs this surface actually uses. Widen it when an operation needs one. */
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

/**
 * Response headers, already lower-cased by the transport.
 *
 * A plain record rather than the DOM `Headers` class, because the mock transport has no `Headers`
 * to hand back and a test should not have to build one to assert on `Retry-After`.
 */
export type HttpHeaders = Readonly<Record<string, string>>;

export interface HttpRequest {
  readonly method: HttpMethod;
  /** Path under the API base, always starting with a slash: `/v1/handoff/exchange`. */
  readonly path: string;
  /** Serialised as JSON. Absent means no body at all, not an empty one. */
  readonly body?: unknown;
  /**
   * The caller's Firebase ID token. Absent on the operations that declare `security: []`.
   * Never a cookie, never a query parameter — a credential in a URL ends up in logs and history.
   */
  readonly bearerToken?: string;
}

/**
 * What came back. `body` is whatever the response decoded to, or `null` when there was no body or
 * it was not JSON — a caller must never assume it parsed.
 */
export interface HttpResponse {
  readonly status: number;
  readonly headers: HttpHeaders;
  readonly body: unknown;
}

/**
 * A transport either produces an HTTP answer or throws `NetworkUnreachable`. It never throws for
 * a 4xx or a 5xx: a refusal is an answer, and turning it into an exception is how the 503 rule
 * gets lost somewhere in a catch block.
 */
export type Transport = (request: HttpRequest, signal?: AbortSignal) => Promise<HttpResponse>;

/**
 * No HTTP answer at all — DNS, TLS, an offline laptop, a CORS pre-flight the browser refused.
 *
 * This is NOT the same thing as a 503, and the difference matters: a 503 is the platform telling
 * us it could not verify a token right now, which the corpus rules must never sign anyone out
 * (adr/0010-firebase-integration.md). An unreachable network is us not knowing anything. Both
 * are retried and neither signs anyone out, but only one of them carries a `Retry-After`.
 */
export class NetworkUnreachable extends Error {
  public constructor(cause?: unknown) {
    super('The cabinet could not reach the MoonX API.');
    this.name = 'NetworkUnreachable';
    this.cause = cause;
  }
}

/** Case-insensitive header read. HTTP header names are case-insensitive; ours arrive lowered. */
export function headerValue(headers: HttpHeaders, name: string): string | null {
  return headers[name.toLowerCase()] ?? null;
}
